import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building, Repeat, UserRound, Palette, LogOut,
  ChevronDown, CheckCircle2, Check, Building2, PlayCircle,
} from "lucide-react";
import { useKuizyTheme } from "@/styles/kuizyTheme";
import { API_BASE } from "@/settings/master";
import ProfileDrawer from "@/components/learners/ProfileDrawer";
import SwitchOrgModal from "@/widgets/SwitchOrgModal";
import { logout as apiLogout, learnerMemberships, changeOrg, fetchMemberships, adminToken } from "@/api/auth";
import { logoutLearner } from "@/api/kuizy";

const pop = { initial: { opacity: 0, scale: 0.96 }, animate: { opacity: 1, scale: 1 }, transition: { duration: 0.22 } };
const Dot = ({ className = "" }) => <span className={`inline-block w-3 h-3 rounded-full ${className}`} />;

/**
 * LearnerLayout
 * Shared top-nav header for all learner pages.
 *
 * Props:
 *   profile        – user profile object (display_name, avatar_url, username, ...)
 *   onProfileSaved – callback after ProfileDrawer saves successfully
 *   children       – page content rendered below the header
 */
export default function LearnerLayout({ profile, onProfileSaved, children }) {
  const nav = useNavigate();
  const { t, theme, setTheme } = useKuizyTheme();

  // ── UI state ────────────────────────────────────────────────────────────────
  const [menuOpen, setMenuOpen] = useState(false);
  const [themePanelOpen, setThemePanelOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const menuRef = useRef(null);

  // ── Learner org (switcher for learner memberships) ───────────────────────
  const [memberships, setMemberships] = useState([]);
  const [activeOrgId, setActiveOrgId] = useState("");
  const [orgModalOpen, setOrgModalOpen] = useState(false);
  const [tempOrgId, setTempOrgId] = useState("");
  const [orgSearch, setOrgSearch] = useState("");

  // ── Admin role / org (for users who are also admin) ──────────────────────
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminMemberships, setAdminMemberships] = useState([]);
  const [switchOrgOpen, setSwitchOrgOpen] = useState(false);
  const [adminPage, setAdminPage] = useState(1);
  const [adminHasMore, setAdminHasMore] = useState(true);
  const [adminSearch, setAdminSearch] = useState("");

  // ── Data loading ─────────────────────────────────────────────────────────
  const loadAdminMemberships = useCallback(async ({ pageNumber = 1, search = "", reset = false } = {}) => {
    try {
      const { data } = await fetchMemberships({ page: pageNumber, per_page: 10, search });
      if ((data?.items?.length ?? 0) > 0) setIsAdmin(true);
      setAdminMemberships(prev => {
        if (reset) return data?.items || [];
        const map = new Map();
        prev.forEach(i => map.set(i.organization_id, i));
        (data?.items || []).forEach(i => map.set(i.organization_id, i));
        return Array.from(map.values());
      });
      setAdminPage(data?.page || 1);
      setAdminHasMore((data?.page || 1) < (data?.total_pages || 1));
    } catch (err) {
      console.error("loadAdminMemberships:", err);
    }
  }, []);

  useEffect(() => {
    // Learner memberships (org switcher pill)
    (async () => {
      try {
        const r = await learnerMemberships();
        setMemberships(r?.data?.items || []);
      } catch { /* silent */ }
    })();

    setActiveOrgId(localStorage.getItem("orgId") || "");
    loadAdminMemberships({ pageNumber: 1, reset: true });
  }, [loadAdminMemberships]);

  // Close menu on outside click
  useEffect(() => {
    const fn = e => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
        setThemePanelOpen(false);
      }
    };
    window.addEventListener("click", fn);
    return () => window.removeEventListener("click", fn);
  }, [menuOpen]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const filteredLearnerOrgs = useMemo(() => {
    const q = (orgSearch || "").trim().toLowerCase();
    if (!q) return memberships;
    return memberships.filter(m =>
      (m.org_name_th || "").toLowerCase().includes(q) ||
      (m.org_name_en || "").toLowerCase().includes(q)
    );
  }, [memberships, orgSearch]);

  const activeOrgName =
    memberships.find(m => m.org_id === activeOrgId)?.org_name_th ||
    memberships.find(m => m.org_id === activeOrgId)?.org_name_en ||
    "ไม่พบข้อมูล";

  // ── Avatar ────────────────────────────────────────────────────────────────
  const avatarUrl = profile?.avatar_url
    ? (/^(http|data:)/.test(profile.avatar_url) ? profile.avatar_url : `${API_BASE}${profile.avatar_url}`)
    : "";
  const avatarText = (profile?.display_name || profile?.name || profile?.username || "?").slice(0, 1).toUpperCase();
  const avatarNode = avatarUrl
    ? <img src={avatarUrl} alt="me" className="w-8 h-8 rounded-full object-cover" />
    : <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary text-white text-sm font-bold">{avatarText}</span>;

  // ── Actions ───────────────────────────────────────────────────────────────
  const doLogout = useCallback(async () => {
    try { await logoutLearner(); } catch { }
    try { await apiLogout(); } catch { }
    localStorage.clear();
    nav("/learn/login", { replace: true });
  }, [nav]);

  const goAdmin = async (org) => {
    try {
      const res = await adminToken(org?.org_id || org?.organization_id);
      if (res?.data?.success === true) {
        localStorage.setItem("learner", "0");
        localStorage.setItem("orgId", res?.data?.admin_org_id || org.org_id);
        window.location.replace("/dashboard");
      }
    } catch (err) {
      console.error("adminToken failed", err);
    }
  };

  const confirmChangeOrg = async () => {
    if (!tempOrgId) return;
    try {
      const { data: org } = await changeOrg(tempOrgId);
      if (org?.active_org_id) localStorage.setItem("orgId", org.active_org_id);
      if (org?.roles) localStorage.setItem("roles", JSON.stringify(org.roles));
      setActiveOrgId(org?.active_org_id || "");
      setOrgModalOpen(false);
      setOrgSearch("");
      window.location.href = "/learn/home";
    } catch (err) {
      console.error("changeOrg failed", err);
    }
  };

  // ── Sub-components ────────────────────────────────────────────────────────
  const ThemeRow = ({ id, label, dot }) => (
    <button
      onClick={() => { setTheme(id); setThemePanelOpen(false); setMenuOpen(false); }}
      className={`w-full h-9 px-3 rounded-lg flex items-center justify-between hover:bg-black/5 text-sm ${theme === "dark" ? "text-white hover:bg-white/10" : "text-gray-700"}`}
    >
      <span className="flex items-center gap-2">
        <Dot className={dot} />
        <span>{label}</span>
      </span>
      {theme === id ? <CheckCircle2 className="size-4 text-primary" /> : null}
    </button>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={`min-h-screen ${t.bg}`}>

      {/* ── Sticky Header ─────────────────────────────────────────────────── */}
      <header className={`sticky top-0 z-30 border-b ${theme === "dark" ? "bg-black/30 border-white/10 backdrop-blur" : "bg-white border-gray-200"}`}>
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-3">

            {/* Left: Logo + Org pill */}
            <div className="flex items-center gap-2">
              <Link to="/learn/home">
                <img src="/images/app-logo.png" alt="Kuizy" className="h-11" />
              </Link>
              <div className="hidden md:block px-4 py-3 rounded-3xl border ml-2 border-gray-200 bg-gray-100 shadow-sm">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex gap-3">
                    <Building className="w-5 h-5 text-gray-500" />
                    <div className="hidden lg:block text-sm text-gray-500 mb-1">องค์กร</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setTempOrgId(activeOrgId); setOrgModalOpen(true); }}
                    className="p-1 text-gray-400 hover:text-gray-600"
                    title="สลับองค์กร"
                  >
                    <Repeat className="w-4 h-4" />
                  </button>
                </div>
                <div className="text-sm font-medium text-gray-800 max-w-[150px] truncate">
                  {activeOrgName}
                </div>
              </div>
            </div>

            {/* Right: Role toggle + Profile menu */}
            <div className="flex items-center gap-3">

              {/* Admin ↔ Learner toggle (only visible if user is also admin) */}
              {isAdmin && (
                <div className="hidden sm:flex items-center gap-2">
                  <span className="text-xs font-semibold text-primary/70">ผู้ดูแลระบบ</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={true}
                      onChange={() => setSwitchOrgOpen(true)}
                      className="sr-only peer"
                    />
                    <div className="w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-secondary transition-colors duration-200" />
                    <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transform transition-transform duration-200 peer-checked:translate-x-5" />
                  </label>
                  <span className="text-xs font-semibold text-secondary">ผู้เรียน</span>
                </div>
              )}

              {/* Profile dropdown */}
              <div className="relative" ref={menuRef}>
                <button
                  onClick={e => { e.stopPropagation(); setMenuOpen(v => !v); setThemePanelOpen(false); }}
                  className="flex items-center gap-2 bg-gray-100 border border-gray-200 rounded-full pl-2 pr-3 py-1 hover:bg-gray-200 focus:ring-2 focus:ring-primary"
                >
                  {avatarNode}
                  <span className="max-w-[100px] sm:max-w-[160px] truncate text-sm font-medium text-gray-800 hidden sm:block">
                    {profile?.display_name || profile?.name || profile?.username || "Me"}
                  </span>
                  <ChevronDown className={`size-4 transition ${menuOpen ? "rotate-180" : ""}`} />
                </button>

                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-md shadow-lg ring-1 ring-black/5 z-30 p-2">
                    <button
                      onClick={() => { setMenuOpen(false); setDrawerOpen(true); }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-gray-700 hover:bg-gray-100 text-sm rounded-md"
                    >
                      <UserRound className="size-4" />
                      <span>แก้ไขโปรไฟล์</span>
                    </button>
                    <button
                      onClick={() => setThemePanelOpen(o => !o)}
                      className="flex items-center justify-between w-full px-3 py-2 text-gray-700 hover:bg-gray-100 text-sm rounded-md"
                    >
                      <span className="flex items-center gap-2">
                        <Palette className="size-4" />
                        <span>ธีม</span>
                      </span>
                      <ChevronDown className={`size-4 transition ${themePanelOpen ? "rotate-180" : ""}`} />
                    </button>
                    {themePanelOpen && (
                      <div className="mt-1 mb-2 space-y-1 px-1">
                        <ThemeRow id="dark"      label="ดำ"   dot="bg-gray-900" />
                        <ThemeRow id="white"     label="ขาว"  dot="bg-gray-200" />
                        <ThemeRow id="primary"   label="ชมพู" dot="bg-pink-500" />
                        <ThemeRow id="secondary" label="ฟ้า"  dot="bg-blue-500" />
                      </div>
                    )}
                    <div className="h-px bg-gray-100 my-1" />
                    <button
                      onClick={() => { setMenuOpen(false); setLogoutOpen(true); }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-red-600 hover:bg-red-50 text-sm rounded-md"
                    >
                      <LogOut className="size-4" />
                      <span>ออกจากระบบ</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ── Page content ──────────────────────────────────────────────────── */}
      {children}

      {/* ── Profile Drawer ────────────────────────────────────────────────── */}
      <ProfileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        profile={profile}
        onSaved={onProfileSaved}
      />

      {/* ── Learner Org Modal ─────────────────────────────────────────────── */}
      {orgModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOrgModalOpen(false)} />
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-lg p-4 z-10">
            <h3 className="text-lg font-semibold mb-3">เลือกองค์กร</h3>
            <div className="mb-3">
              <input
                type="search"
                placeholder="ค้นหาองค์กร (ภาษาอังกฤษ/ไทย)"
                value={orgSearch}
                onChange={e => setOrgSearch(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-2 focus:ring-primary focus:outline-none"
              />
            </div>
            <div className="max-h-64 overflow-y-auto space-y-3">
              {filteredLearnerOrgs.map(m => {
                const active = tempOrgId === m.org_id;
                return (
                  <button
                    key={m.organization_id}
                    type="button"
                    onClick={() => setTempOrgId(m.org_id)}
                    className={[
                      "w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition text-left cursor-pointer",
                      active
                        ? "bg-pink-50 border-pink-500 shadow-[0_0_0_3px_rgba(236,72,153,0.15)]"
                        : "bg-white border-gray-200 hover:bg-gray-50",
                    ].join(" ")}
                  >
                    <div className={["h-10 w-10 rounded-xl flex items-center justify-center", active ? "bg-pink-500 text-white" : "bg-pink-100 text-pink-600"].join(" ")}>
                      <Building2 size={24} />
                    </div>
                    <div className="flex-1">
                      <div className="text-base font-medium">{m.org_name_th || m.org_name_en}</div>
                      {m.org_name_en && <div className="text-sm text-gray-500">{m.org_name_en}</div>}
                    </div>
                    <div className={["h-6 w-6 rounded-full border flex items-center justify-center", active ? "bg-pink-500 border-pink-500" : "bg-white border-gray-300"].join(" ")}>
                      {active && <Check size={16} color="#ffffff" />}
                    </div>
                    <input type="radio" name="org-radio" value={m.org_id} checked={active} onChange={() => setTempOrgId(m.org_id)} className="sr-only" />
                  </button>
                );
              })}
              {filteredLearnerOrgs.length === 0 && (
                <div className="text-sm text-gray-500">ไม่พบองค์กรที่ค้นหา</div>
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setOrgModalOpen(false)} className="py-2 px-3 rounded-md bg-gray-100 cursor-pointer">
                ยกเลิก
              </button>
              <button type="button" onClick={confirmChangeOrg} className="py-2 px-4 rounded-md bg-primary text-white cursor-pointer">
                ยืนยัน
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Admin Org Switch Modal (switch back to admin) ─────────────────── */}
      <SwitchOrgModal
        open={switchOrgOpen}
        title="เลือกองค์กร"
        items={adminMemberships}
        loading={false}
        hasMore={adminHasMore}
        onSearch={q => {
          setAdminSearch(q);
          loadAdminMemberships({ pageNumber: 1, search: q, reset: true });
        }}
        onLoadMore={() => loadAdminMemberships({ pageNumber: adminPage + 1, search: adminSearch })}
        onClose={() => setSwitchOrgOpen(false)}
        onSelect={async org => {
          setSwitchOrgOpen(false);
          if (isAdmin) await goAdmin(org);
        }}
      />

      {/* ── Logout Confirmation Modal ─────────────────────────────────────── */}
      {logoutOpen && (
        <div className="fixed inset-0 z-[60] grid place-items-center bg-black/50 p-4">
          <motion.div {...pop} className={`w-full max-w-sm rounded-2xl p-5 ${t.card}`}>
            <div className={`text-base font-semibold ${t.text}`}>ยืนยันการออกจากระบบ</div>
            <div className={`text-sm mt-2 ${t.subtext}`}>คุณต้องการออกจากระบบใช่หรือไม่</div>
            <div className="mt-5 grid grid-cols-2 gap-2">
              <button onClick={() => setLogoutOpen(false)} className="h-10 rounded-xl bg-white/10">ยกเลิก</button>
              <button onClick={doLogout} className="h-10 rounded-xl bg-danger text-white flex items-center justify-center gap-2">
                <PlayCircle className="size-4" /> ยืนยัน
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
